import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { fetchProgram, fetchsubmission } from '../../../utils/axios';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText, ChevronRight, Check } from 'lucide-react';
import { useSelector } from 'react-redux';
import { getUserInfo } from '../../../reducers/authSlice';
import ProgramReport from '../../common/ReportsTypes/ProgramReport';
import './SelectReport.css'

const SelectionCard = ({ onGenerateReport, isLoading = false }) => {

  const {id} = useParams();
  const [reportType, setReportType] = useState('technical');
  const [selectedItem, setSelectedItem] = useState(null);
  const [programOptions, setProgramOptions] = useState([]);
  const [submissionOptions, setSubmissionOptions] = useState([]);
  const [isOptionsLoading, setIsOptionsLoading] = useState(false);
  const [selectSubmission, setSelectSubmission] = useState([]);


  const { userInfo } = useSelector(getUserInfo);
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsOptionsLoading(true);
        let response;
  
        if (reportType === 'technical'|| 'executive') {
          response = await fetchProgram();
          setProgramOptions(
            response.filter(
              (program) =>
                program.program_status === 'approved' ||
                program.program_status === 'closed'
            )
          );
        } 
        // else {
        //   response = await fetchsubmission();
        //   setSubmissionOptions(
        //     response.results.filter(
        //       (submission) => submission.submission_status === 'resolved'
        //     )
        //   );
        // }
      } catch (err) {
        console.error('Failed to fetch options:', err);
      } finally {
        setIsOptionsLoading(false);
      }
    };
  
    loadData();
  }, [reportType]);
  
  useEffect(() => {
  const loadSubmissions = async () => {
    try {
      const response = await fetchsubmission();
      setSelectSubmission(response || []);
    } catch (err) {
      console.error('Failed to load submission data:', err);
    }
  };

  loadSubmissions();
}, [id]);


 const handleSubmit = () => {
  let userRole = userInfo?.role === 'sub_admin' ? 'admin' : userInfo.role;

  if (selectedItem) {
    navigate(`/${userRole}/reports-dashboard/${reportType}/${selectedItem.id}`, {
      state: {
        reportdata: selectedItem,
        reportType: reportType,
        selectSubmission: selectSubmission,
      },
    });
  }
};



  return (
    <div className="selector">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="card mx-auto shadow-lg rounded-4 overflow-hidden"
        style={{ maxWidth: '768px' }}
      >
        <div className="card-header bg-primary text-white p-4">
          <h5 className="mb-1 fw-bold">Generate Your Report</h5>
          <p className="mb-0 opacity-75">Select your report type and specific item to generate a detailed report</p>
        </div>

        <div className="card-body">
          {/* Report Type Selector */}
          <div className="mb-4">
            <label className="form-label">Report Type</label>
            <div className="row g-3">
              {['technical', 'executive'].map((type) => (
                <div className="col-6" key={type}>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setReportType(type)}
                    className={`btn w-100 d-flex justify-content-between align-items-center border ${
                      reportType === type
                        ? 'border-primary bg-light text-primary'
                        : 'border-secondary bg-white text-dark'
                    }`}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <div
                        className="rounded-circle d-flex justify-content-center align-items-center bg-light"
                        style={{ width: '32px', height: '32px' }}
                      >
                        <FileText
                          size={16}
                          className={reportType === type ? 'text-primary' : 'text-muted'}
                        />
                      </div>
                      <span className="fw-medium text-capitalize">{type}-based</span>
                    </div>
                    {reportType === type && <Check size={20} className="text-primary" />}
                  </motion.button>
                </div>
              ))}
            </div>
          </div>

          {/* Options Section */}
          <div className="mb-4">
            <label className="form-label">Select {reportType === 'technical' ? 'technical' : 'executive'}</label>
            {isOptionsLoading ? (
              <div className="d-flex align-items-center justify-content-center py-4">
                <div className="spinner-border text-primary me-2" role="status" style={{ width: '1.5rem', height: '1.5rem' }}></div>
                <span className="text-muted">Loading options...</span>
              </div>
            ) : (
              <div className="overflow-auto" style={{ maxHeight: '240px' }}>
                {(reportType === 'technical' || 'executive' ? programOptions : null).map((option, index) => (
                  <motion.button
                    key={index}
                    onClick={() => {
                      setSelectedItem(option);
                      
                    }}
                    className={`btn w-100 mb-2 text-start d-flex justify-content-between align-items-center ${
                      selectedItem?.id === option.id ? 'bg-light border border-primary text-primary' : 'bg-white border border-secondary text-dark'
                    }`}
                  >
                    {reportType === 'technical' && <span>{option.program_title} ({option.id})</span>}
                    {reportType === 'executive' && <span>{option.program_title} ({option.id})</span>}
                    {selectedItem?.id === option.id ? (
                      <Check size={18} className="text-primary" />
                    ) : (
                      <ChevronRight size={18} className="text-muted" />
                    )}
                  </motion.button>
                ))}
              </div>
            )}
          </div>

          {/* Generate Button */}
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleSubmit}
            disabled={!selectedItem || isLoading || isOptionsLoading}
            className={`btn w-100 py-2 fw-medium d-flex justify-content-center align-items-center gap-2 ${
              selectedItem && !isLoading && !isOptionsLoading ? 'btn-primary' : 'btn-secondary disabled text-muted'
            }`}
          >
            {isLoading ? (
              <>
                <div className="spinner-border spinner-border-sm text-light" role="status"></div>
                <span>Generating Report...</span>
              </>
            ) : (
              <>
                <FileText size={18} />
                <span>Generate Report</span>
              </>
            )}
          </motion.button>
        </div>
      </motion.div>

      {/* Pass the fetched reportdata to Report component */}
      {/* <ProgramReport
  reportType={reportType}
  reportData={selectedItem}
  programName={reportType === 'program' ? selectedItem?.program_title || 'Unknown Program' : ''}
  submissionName={reportType === 'submission' ? selectedItem?.submission_title || 'Unknown Submission' : ''}
/> */}

    </div>
  );
};

export default SelectionCard;
